import { useEffect, useRef, useState } from "react";
import { Avatar, Button, FileField, InlineError } from "../components/index.ts";
import { CropAdjust } from "./CropAdjust.tsx";
import { PhotoDropZone } from "./PhotoDropZone.tsx";
import type { Square } from "./face-boxes.ts";
import {
  ACCEPTED_PHOTO_TYPES,
  centreSquare,
  cropToJpeg,
  PhotoFileError,
  readPhotoFile,
  releasePhoto,
  type PhotoSource,
} from "./photo-file.ts";

/**
 * One person's profile photo: what it is now, a new photo with the crop to fix, and remove. Used
 * for your own photo (Settings, the staff day), for a participant on the owner's member profile,
 * and by a family for their own people.
 *
 * Nothing changes until "Save photo". The file never leaves the browser: only the 512 x 512 crop
 * is sent.
 */
export function SinglePhotoEditor({
  name,
  photoUrl,
  onSave,
  onRemove,
  saving,
  removing,
  error,
  blockedMessage,
  label = "Profile photo",
}: {
  name: string;
  photoUrl: string | null;
  /** Gets the JPEG as base64. Calls `done` when the save landed. */
  onSave: (jpeg: string, done: () => void) => void;
  onRemove: () => void;
  saving: boolean;
  removing: boolean;
  error: unknown;
  /** Set when a photo cannot be added (the family said no). Shown in place of the file picker. */
  blockedMessage?: string;
  label?: string;
}) {
  const [source, setSource] = useState<PhotoSource | null>(null);
  const [square, setSquare] = useState<Square | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [pickCount, setPickCount] = useState(0);

  // Counts picks: a read that finishes after a newer pick is thrown away.
  const pickId = useRef(0);

  async function pick(file: File) {
    pickId.current += 1;
    const id = pickId.current;
    setReadError(null);
    try {
      const photo = await readPhotoFile(file);
      if (id !== pickId.current) {
        releasePhoto(photo);
        return;
      }
      setSource(photo);
      setSquare(centreSquare(photo));
    } catch (caught) {
      if (id !== pickId.current) return;
      setReadError(caught instanceof PhotoFileError ? caught.message : "We cannot open this photo. Save it as a JPG and try again.");
    }
  }

  // The preview of a picked photo is freed when the photo changes or the screen closes.
  useEffect(() => () => releasePhoto(source), [source]);
  // Closing the screen makes every read still running stale, so its photo is freed when it ends.
  useEffect(
    () => () => {
      pickId.current += 1;
    },
    [],
  );

  function close() {
    setSource(null);
    setSquare(null);
    setPickCount((count) => count + 1);
  }

  function save() {
    if (!source || !square) return;
    try {
      onSave(cropToJpeg(source, square).base64, close);
    } catch (caught) {
      setReadError(caught instanceof PhotoFileError ? caught.message : "We cannot make this photo. Try another photo.");
    }
  }

  if (source && square) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-[14px] text-ink-2">Drag the photo so the face is in the middle of the circle.</p>
        <CropAdjust
          source={source}
          square={square}
          minSide={Math.min(source.width, source.height) / 8}
          onChange={setSquare}
          size={180}
        />
        {error ? <InlineError error={error} /> : null}
        {readError ? <p className="text-[13px] text-cancelled-ink">{readError}</p> : null}
        <div className="flex flex-wrap gap-2">
          <Button type="button" onClick={save} loading={saving}>
            Save photo
          </Button>
          <Button type="button" variant="secondary" onClick={close} disabled={saving}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PhotoDropZone onFile={(file) => void pick(file)} onReject={setReadError} disabled={Boolean(blockedMessage)}>
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-4">
        <Avatar name={name} photoUrl={photoUrl} size={72} />
        <div className="flex min-w-0 flex-1 flex-col items-start gap-2">
          {blockedMessage ? (
            <p className="text-[14px] text-ink-2">{blockedMessage}</p>
          ) : (
            <FileField
              key={pickCount}
              label={label}
              hideLabel
              hint="A JPG, PNG or WebP photo, or drag one here. Only the circle is saved."
              accept={ACCEPTED_PHOTO_TYPES}
              buttonLabel={photoUrl ? "Choose a new photo" : "Choose a photo"}
              showFileName={false}
              error={readError ?? undefined}
              onFile={(file) => void pick(file)}
            />
          )}
          {photoUrl ? (
            <Button type="button" variant="ghost" onClick={onRemove} loading={removing}>
              Remove photo
            </Button>
          ) : null}
        </div>
      </div>
      {error ? <InlineError error={error} /> : null}
    </div>
    </PhotoDropZone>
  );
}
